import type { FilterDefinition } from "@/components/StructuredSearch/config/structured-search-schema";
import type { Filter } from "@/components/StructuredSearch/config/types";
import { StructuredSearchField } from "@/components/StructuredSearch/config/types";

const OLDER_DATA_SUPPORTED_FIELDS = new Set<string>([
  "prompt_id",
  "pl_id",
  StructuredSearchField.Status,
  StructuredSearchField.Tags,
  StructuredSearchField.Metadata,
  StructuredSearchField.Signal,
]);

const OLDER_DATA_ALLOWED_OPERATORS: Record<string, readonly string[]> = {
  prompt_id: ["is", "in"],
  pl_id: ["is"],
  [StructuredSearchField.Status]: ["is", "is_not", "in", "not_in"],
  [StructuredSearchField.Tags]: ["contains", "in"],
  [StructuredSearchField.Metadata]: ["key_equals", "in"],
  [StructuredSearchField.Signal]: ["key_equals"],
};

export const OLDER_DATA_UNAVAILABLE_COPY =
  "Some filters are unavailable because this search is querying older data.";

export const isOlderDataSearch = (
  dateRange: { from?: Date; to?: Date } | undefined,
  cutoverDate: string | null | undefined,
): boolean => {
  if (!cutoverDate) {
    return true;
  }

  const parsedCutoverDate = new Date(cutoverDate);
  if (Number.isNaN(parsedCutoverDate.getTime())) {
    return true;
  }

  return (
    !!dateRange?.from &&
    !Number.isNaN(dateRange.from.getTime()) &&
    dateRange.from.getTime() < parsedCutoverDate.getTime()
  );
};

export const isPostgresOnlySearch = ({
  dateRange,
  cutoverDate,
}: {
  dateRange: { from?: Date; to?: Date } | undefined;
  cutoverDate: string | null | undefined;
}): boolean => isOlderDataSearch(dateRange, cutoverDate);

export const isOlderDataFieldSupported = (field: string | undefined): boolean =>
  !!field && OLDER_DATA_SUPPORTED_FIELDS.has(field);

export const getOlderDataAllowedOperators = (field: string | undefined): string[] =>
  field ? [...(OLDER_DATA_ALLOWED_OPERATORS[field] ?? [])] : [];

export const isOlderDataOperatorSupported = (
  field: string | undefined,
  operator: string | undefined,
): boolean =>
  !!field &&
  !!operator &&
  isOlderDataFieldSupported(field) &&
  (OLDER_DATA_ALLOWED_OPERATORS[field]?.includes(operator) ?? false);

export const isOlderDataCompatibleFilter = (filter: Filter): boolean =>
  isOlderDataOperatorSupported(filter.field, filter.operator);

export const sanitizeFiltersForOlderData = (filters: Filter[]): Filter[] =>
  filters.every(isOlderDataCompatibleFilter)
    ? filters
    : filters.filter(isOlderDataCompatibleFilter);

export const partitionFieldsByOlderDataSupport = (
  fields: FilterDefinition[],
): {
  supported: FilterDefinition[];
  unsupported: FilterDefinition[];
} =>
  fields.reduce(
    (result, field) => {
      if (isOlderDataFieldSupported(field.key)) {
        result.supported.push(field);
      } else {
        result.unsupported.push(field);
      }
      return result;
    },
    {
      supported: [] as FilterDefinition[],
      unsupported: [] as FilterDefinition[],
    },
  );
